import { useState } from "react";
import { LuCircleHelp, LuLoaderCircle, LuUpload } from "react-icons/lu";
import { useProjectModal } from "../../../providers/ProjectModalProvider";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import {
  useIsIconUploading,
  useOriginalIconImage,
  useUploadedIconImage,
} from "../../../view_stores/useProjectIconStore";
import ProjectIconDeleteConfirmation from "./ProjectIconDeleteConfirmation";

export default function ProjectIconContainer({
  imageDropped,
}: {
  imageDropped: (imageFile: File) => void;
}) {
  const originalImage = useOriginalIconImage();
  const uploadedImage = useUploadedIconImage();
  const isUploading = useIsIconUploading();
  const { show } = useProjectModal();
  const [isDragOver, setIsDragOver] = useState(false);
  const { showSnackbar } = useSnackbar();

  const handleDelete = () => {
    show({
      children: <ProjectIconDeleteConfirmation />,
      className: "max-w-[500px]",
    });
  };

  const handleDragEvent = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === "dragenter") {
      setIsDragOver(true);
    } else if (e.type === "dragleave") {
      // Get the related target (the element we're moving to)
      const relatedTarget = e.relatedTarget as HTMLElement;

      // If we're moving to an element that's a child of our drop zone, ignore the event
      if (e.currentTarget.contains(relatedTarget)) {
        return;
      }

      setIsDragOver(false);
    }
  };

  const iconToShow = uploadedImage
    ? URL.createObjectURL(uploadedImage)
    : originalImage;

  return (
    <div
      className="w-[250px] h-[250px]
          rounded-[12px] mt-[175px] mx-auto
          bg-[#F2F3FD] dark:bg-[#1E2023]
          flex items-center justify-center
          shrink-0 relative
          shadow-[0_2px_8.1px_-4px_rgba(0,0,0,0.12),0_2px_5.9px_0_rgba(0,0,0,0.18)]"
      onDragEnter={handleDragEvent}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={handleDragEvent}
      onDrop={async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const files = Array.from(e.dataTransfer.files);
        const imageFile = files.find(
          (file) =>
            file.type.startsWith("image/") &&
            ["image/jpeg", "image/png", "image/jpg"].includes(file.type)
        );

        if (imageFile) {
          imageDropped(imageFile);
        } else {
          showSnackbar("This image format is not supported", {
            type: "error",
          });
        }
      }}
    >
      <div className="w-full h-full overflow-hidden rounded-[12px] relative">
        {isUploading || !originalImage ? (
          <div
            className="absolute inset-0 rounded-[12px] 
              flex items-center justify-center z-10"
          >
            <LuLoaderCircle className="animate-spin" size={40} />
          </div>
        ) : (
          <img
            src={iconToShow!}
            alt="App Icon"
            className="w-full h-full object-cover"
          />
        )}
      </div>
      {/* {originalImage && (
        <button
          className="absolute top-10 -right-8 
            hover:opacity-75 hover:cursor-pointer 
            transition-all duration-300"
          onClick={handleDelete}
        >
          <LuTrash2 size={20} />
        </button>
      )} */}
      {isDragOver && !isUploading && (
        <div
          className="absolute inset-0 bg-black/50 
            flex flex-col items-center justify-center
            z-20 backdrop-blur-sm border-2 border-dashed
            rounded-[12px] px-1"
        >
          <LuUpload className="text-white mb-3" size={32} />
          <p className="text-white text-xl font-medium text-center">
            Drop image here
          </p>
          <p className="text-white/75 text-sm text-center">
            Supports JPEG and PNG
          </p>
          <p className="text-white/75 text-sm text-center">
            Minimum required size is 512×512 pixels and maximum is 4096×4096
            pixels.
          </p>
        </div>
      )}
      <div className="absolute top-1 -right-8 flex items-center gap-2">
        <span data-tooltip-id="icon-size-tooltip">
          <LuCircleHelp size={20} />
        </span>
      </div>
    </div>
  );
}
